import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'enhanced-light' | 'enhanced-dark' | 'system'

// Theme migration mapping for existing users
const themeMigration: Record<string, Theme> = {
    'light': 'light',
    'dark': 'dark',
    'slate': 'light',
    'blue': 'light',
    'emerald': 'light',
    'violet': 'light',
    'rose': 'light',
    'ocean': 'light',
    'amber': 'light',
    'high-contrast-light': 'enhanced-light',
    'high-contrast-dark': 'enhanced-dark',
    'system': 'system',
}

interface UiState {
    theme: Theme
    sidebarCollapsed: boolean
    sidebarOpen: boolean
}

interface UiActions {
    setTheme: (theme: Theme) => void
    toggleSidebar: () => void
    setSidebarCollapsed: (collapsed: boolean) => void
    setSidebarOpen: (open: boolean) => void
}

type UiStore = UiState & UiActions

/**
 * Apply theme by managing CSS classes only.
 * All theme variables are defined in globals.css.
 */
const applyTheme = (theme: Theme, skipMigration = false) => {
    const root = document.documentElement

    // Migrate old themes to new ones (unless migration is skipped to avoid loops)
    const migratedTheme = skipMigration ? theme : (themeMigration[theme] || theme)

    // Remove existing theme classes and attributes
    root.classList.remove('dark', 'enhanced-light', 'enhanced-dark', 'high-contrast-light', 'high-contrast-dark')
    root.removeAttribute('data-theme')

    // Determine which theme to apply
    let effectiveTheme = migratedTheme

    if (migratedTheme === 'system') {
        // Check system preference
        effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }

    // Apply dark class if needed
    if (effectiveTheme === 'dark' || effectiveTheme === 'enhanced-dark') {
        root.classList.add('dark')
    }

    // Apply enhanced theme classes if needed
    if (effectiveTheme === 'enhanced-light' || effectiveTheme === 'enhanced-dark') {
        root.classList.add(effectiveTheme)
        root.setAttribute('data-theme', effectiveTheme)
    }

    // CSS automatically handles all theme variables via .dark class and other selectors
}

/**
 * Setup listener for system theme changes when in system mode
 */
const setupSystemThemeListener = (getCurrentTheme: () => Theme, setTheme: (theme: Theme) => void) => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
        if (getCurrentTheme() === 'system') {
            applyTheme('system', true)
        }
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
}

export const useUiStore = create<UiStore>()(
    persist(
        (set, get) => ({
            // State
            theme: 'light',
            sidebarCollapsed: false,
            sidebarOpen: true,

            // Actions
            setTheme: (theme) => {
                const migratedTheme = themeMigration[theme] || theme
                set({ theme: migratedTheme as Theme })
                applyTheme(migratedTheme as Theme, true)
            },

            toggleSidebar: () => {
                const { sidebarCollapsed } = get()
                set({ sidebarCollapsed: !sidebarCollapsed })
            },

            setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

            setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
        }),
        {
            name: 'ui-storage',
            onRehydrateStorage: () => (state) => {
                if (state) {
                    // Migrate old themes on rehydration
                    const migratedTheme = themeMigration[state.theme] || state.theme
                    if (migratedTheme !== state.theme) {
                        state.theme = migratedTheme as Theme
                    }
                    // Apply theme when store rehydrates
                    applyTheme(migratedTheme as Theme, true)

                    // Setup system theme listener
                    setupSystemThemeListener(
                        () => state.theme,
                        (theme) => state.setTheme(theme)
                    )
                }
            },
        }
    )
)

// Selectors
export const useTheme = () => useUiStore((state) => state.theme)
export const useSidebarCollapsed = () => useUiStore((state) => state.sidebarCollapsed)
export const useSidebarOpen = () => useUiStore((state) => state.sidebarOpen)
